function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function buildPopupHtml(mentor) {
  const parts = [`<div class="mentor-popup">`, `<h3 class="mentor-popup__title">${escapeHtml(mentor.title)}</h3>`];

  if (mentor.categories) {
    parts.push(`<p class="mentor-popup__meta">${escapeHtml(mentor.categories)}</p>`);
  }

  if (mentor.address) {
    parts.push(`<p class="mentor-popup__line">${escapeHtml(mentor.address)}</p>`);
  }

  if (mentor.hours) {
    parts.push(`<p class="mentor-popup__line">${escapeHtml(mentor.hours)}</p>`);
  }

  if (mentor.phone || mentor.email || mentor.url) {
    parts.push('<div class="mentor-popup__links">');
    if (mentor.phone) {
      parts.push(`<a href="tel:${escapeHtml(mentor.phone)}">${escapeHtml(mentor.phone)}</a>`);
    }
    if (mentor.email) {
      parts.push(`<a href="mailto:${escapeHtml(mentor.email)}">${escapeHtml(mentor.email)}</a>`);
    }
    if (mentor.url) {
      parts.push(`<a href="${escapeHtml(mentor.url)}" target="_blank" rel="noopener noreferrer">Website</a>`);
    }
    parts.push('</div>');
  }

  parts.push('</div>');
  return parts.join('');
}

export default function setupMentorLocator() {
  const root = document.querySelector('[data-mentor-locator]');

  if (!root) {
    return;
  }

  const dataNode = root.querySelector('[data-mentor-json]');
  const mapNode = root.querySelector('[data-mentor-map]');
  const listNode = root.querySelector('[data-mentor-list]');
  const token = root.dataset.mapboxToken || '';

  if (!dataNode || !listNode) {
    return;
  }

  const mentors = JSON.parse(dataNode.textContent || '[]').filter((mentor) => mentor.latitude !== null && mentor.longitude !== null);
  const listItems = new Map();
  const markers = new Map();
  let map = null;
  let popup = null;
  let activeMentorId = null;

  root.querySelectorAll('[data-mentor-item]').forEach((item) => {
    listItems.set(Number(item.dataset.mentorId), item);
  });

  function setActiveMentor(mentorId, scrollList = true) {
    if (activeMentorId === mentorId) {
      if (scrollList && listItems.has(mentorId)) {
        listItems.get(mentorId).scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      return;
    }

    if (activeMentorId !== null && listItems.has(activeMentorId)) {
      listItems.get(activeMentorId).classList.remove('is-active');
    }

    if (activeMentorId !== null && markers.has(activeMentorId)) {
      markers.get(activeMentorId).element.classList.remove('is-active');
    }

    activeMentorId = mentorId;

    if (listItems.has(mentorId)) {
      const item = listItems.get(mentorId);
      item.classList.add('is-active');
      if (scrollList) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    if (markers.has(mentorId)) {
      markers.get(mentorId).element.classList.add('is-active');
    }
  }

  function openMentor(mentorId, { flyTo = true, scrollList = true } = {}) {
    const mentor = mentors.find((entry) => entry.id === mentorId);
    if (!mentor || !map || !popup) {
      return;
    }

    setActiveMentor(mentorId, scrollList);
    popup
      .setLngLat([mentor.longitude, mentor.latitude])
      .setHTML(buildPopupHtml(mentor))
      .addTo(map);

    if (flyTo) {
      map.flyTo({
        center: [mentor.longitude, mentor.latitude],
        zoom: Math.max(map.getZoom(), 10),
        essential: true,
      });
    }
  }

  listItems.forEach((item, mentorId) => {
    item.addEventListener('click', () => openMentor(mentorId));
  });

  if (!mapNode || !token) {
    return;
  }

  function initMap() {
    if (typeof window.mapboxgl === 'undefined' || map) {
      return;
    }

    window.mapboxgl.accessToken = token;
    map = new window.mapboxgl.Map({
      container: mapNode,
      style: 'mapbox://styles/mapbox/light-v11',
      attributionControl: true,
    });

    map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
    popup = new window.mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      offset: 18,
    });

    const bounds = new window.mapboxgl.LngLatBounds();

    mentors.forEach((mentor) => {
      const element = document.createElement('button');
      element.className = 'mentor-marker';
      element.type = 'button';
      element.setAttribute('aria-label', mentor.title);

      const marker = new window.mapboxgl.Marker(element)
        .setLngLat([mentor.longitude, mentor.latitude])
        .addTo(map);

      element.addEventListener('click', () => openMentor(mentor.id, { flyTo: false }));

      markers.set(mentor.id, { marker, element });
      bounds.extend([mentor.longitude, mentor.latitude]);
    });

    map.on('load', () => {
      if (mentors.length === 1) {
        map.setCenter([mentors[0].longitude, mentors[0].latitude]);
        map.setZoom(10);
        openMentor(mentors[0].id, { flyTo: false, scrollList: false });
        return;
      }

      map.fitBounds(bounds, {
        padding: { top: 48, right: 48, bottom: 48, left: 48 },
        maxZoom: 11,
      });
    });

    popup.on('close', () => {
      if (activeMentorId !== null && listItems.has(activeMentorId)) {
        listItems.get(activeMentorId).classList.remove('is-active');
      }
      if (activeMentorId !== null && markers.has(activeMentorId)) {
        markers.get(activeMentorId).element.classList.remove('is-active');
      }
      activeMentorId = null;
    });
  }

  if (typeof window.mapboxgl === 'undefined') {
    window.addEventListener('load', initMap, { once: true });
    return;
  }

  initMap();
}
